import { createApp, nextTick } from 'vue';
import Swal from 'sweetalert2/dist/sweetalert2.js';

import Request from '@/common/request';
import utils from '@js/modules/utils/utils';
import base64 from '@js/modules/utils/base64';

const app = createApp({
    data: () => {
        return {
            step: 'ticket', // ticket, child_event, parking

            encrypt_key: null, // 編集の場合のorderのキー
            order_lines: null, // 編集の場合のorderの内容

            accept_status: null,
            need_passport: 1, // 申込みにチケットが必要かどうか？（駐車場のみの受付を可能にする場合は0）
            visitor_categories: null, // 参加者カテゴリの情報
            total_remaining: 0, // 総残数
            child_events: null, // 小イベントの情報
            items: null, // 販売商品の情報
            parking_items: null, // 駐車場の情報
            total_parking_remaining: 0, // 駐車場の総残数

            ticket_num_dict: {}, // 編集の場合のチケットの申込み数の保持枠
            child_event_num_dict: {}, // 編集の場合の小イベントの申込み数の保持枠
            child_event_statuses: null, // 小イベントのステータス
            parking_num_dict: {}, // 駐車場の申込み数の保持枠

            is_parking: false, // 駐車場の申込みがあるかどうか
            is_child_event: false, // 小イベントの申込みがあるかどうか
            is_processing: false, // 処理中かどうか
        };
    },
    computed: {
        next_step() {
            return this.getNextStep();
        },
        prev_step() {
            return this.getPrevStep();
        },
    },
    methods: {
        number_format(num: number | string | null): string {
            if (num === null || num === undefined || num === '') {
                return '';
            }
            const value = Number(num);
            if (!isNaN(value)) {
                return value.toLocaleString();
            }
            return num.toString();
        },
        getUnitName(category: number | string) {
            if (
                category &&
                this.visitor_categories &&
                this.visitor_categories[category] &&
                this.visitor_categories[category]['unit'] !== undefined
            ) {
                return this.visitor_categories[category]['unit']['name'];
            }
            return '人';
        },
        getChildEventUnitName(child: any) {
            if (child && child.node_data && child.node_data.unit !== undefined) {
                return child.node_data.unit['name'];
            }
            return '人';
        },
        sumObjectArray(nums: any) {
            let total = 0;
            Object.keys(nums).forEach(key => {
                let value = nums[key];
                if (value) {
                    value = Number(value);
                    if (!isNaN(value)) {
                        total += value;
                    }
                }
            });
            return total;
        },
        // 次のステップを取得
        getNextStep() {
            if (this.step == 'ticket') {
                // 小イベントがある場合
                if (this.is_child_event) {
                    return 'child_event';
                    // 駐車場がある場合
                } else if (this.is_parking) {
                    return 'parking';
                } else {
                    // 小イベントも駐車場もない場合は、確認画面へ移動
                    return 'confirm';
                }
            } else if (this.step == 'child_event') {
                // 駐車場がある場合
                if (this.is_parking) {
                    return 'parking';
                } else {
                    // 小イベントも駐車場もない場合は、確認画面へ移動
                    return 'confirm';
                }
            } else if (this.step == 'parking') {
                // 駐車場の場合は、確認画面へ移動
                return 'confirm';
            }
        },
        // 前のステップを取得
        getPrevStep() {
            // 小イベントの場合
            if (this.step == 'child_event') {
                return 'ticket';
            } else if (this.step == 'parking') {
                if (this.is_child_event) {
                    // 小イベントの申込み数が0の場合は、チケットの申込み画面へ移動
                    const count = this.sumObjectArray(this.ticket_num_dict);
                    if (count == 0) {
                        return 'ticket';
                    } else {
                        // 小イベントの申込み数が0でない場合は、小イベントの申込み画面へ移動
                        return 'child_event';
                    }
                } else {
                    // 小イベントがない場合は、チケットの申込み画面
                    return 'ticket';
                }
            }
        },
        // 次のステップへ移動
        goNextStep() {
            if (this.is_processing) {
                return;
            }

            Request.clearErrors('order_form');

            this.is_processing = true;

            // 入場券（最初のステップ）の場合
            if (this.step == 'ticket') {
                // カテゴリごとの合計人数をカウント
                const count = this.sumObjectArray(this.ticket_num_dict);
                // パスポートが必須なのにもかかわらず申込み数が0の場合はNG
                if (this.need_passport == 1 && count == 0) {
                    Swal.fire({
                        title: 'エラー',
                        html: '申込み数が選択されていません。',
                        icon: 'warning',
                    });
                    this.is_processing = false;
                    return;
                } else {
                    // 人数チェックした上で、小イベントなどの選択画面に移動
                    utils.loading(true);

                    // 次のステップを取得
                    const next_step = this.getNextStep();
                    Request.post('/api/order/check-order-num', {
                        num: this.ticket_num_dict,
                        encrypt_key: this.encrypt_key,
                        next_step: next_step,
                    })
                        .then(res => {
                            const response = res.data;
                            const result = response.result;
                            if (result == -1) {
                                Swal.fire({
                                    title: 'エラー',
                                    html: '定員に達しているため申込み数の申込みができません。',
                                    icon: 'warning',
                                });
                                return;
                            }
                            // 次のステップは、小イベントや駐車場がある場合によって異なる。
                            if (next_step == 'child_event') {
                                // 小イベントの申込み状況
                                // 形式としては
                                // array[<アトラクションのID>][times]に配列として時間ごとのステータスが入っている
                                // times: {
                                //    time_from: 09:00
                                //    time_to: 10:00
                                //    event_limit: 300
                                //    remaining: 100
                                // }
                                // また、前時間をトータルしての残数も入っている
                                // 例えば、09:00から10:00までの残数が100で、10:00から11:00までの残数が200の場合
                                // 09:00から11:00までのトータルの残数は300となる
                                this.child_event_statuses = response.child_event_statuses;
                                // 子イベントの申込み数がチケットの申込み数を超えている場合は、チケットの申込み数に合わせる
                                Object.keys(this.visitor_categories).forEach(category => {
                                    try {
                                        const ticket_num = this.ticket_num_dict[category];
                                        const child_event = this.child_event_num_dict[category];
                                        // アトラクションの申込みがチケットの申込みを超えている場
                                        if (child_event) {
                                            if (child_event.num > ticket_num) {
                                                child_event.num = ticket_num;
                                            }
                                        }
                                    } catch (e) {
                                        //
                                    }
                                });
                                this.step = 'child_event';
                            } else if (next_step == 'parking') {
                                this.step = 'parking';
                            } else {
                                this.step = 'confirm';
                            }
                        })
                        .catch(err => {
                            Request.showErrorResponse(err, 'order_form');
                        })
                        .finally(() => {
                            utils.loading(false);
                            this.is_processing = false;
                        });

                    // 非同期処理のため、ここで処理は終わる
                    return;
                }
            } else if (this.step == 'child_event') {
                utils.loading(true);
                Request.postAs('/api/order/check-child-events', 'order_form')
                    .then(res => {
                        const response = res.data;
                        const result = response.result;
                        if (result == -1) {
                            this.child_event_statuses = response.child_event_statuses;
                            Swal.fire({
                                title: 'エラー',
                                html: '定員に達しているため申込み数の申込みができません。',
                                icon: 'warning',
                            });
                            this.is_processing = false;
                            return;
                        }
                        const next_step = this.getNextStep();
                        if (next_step == 'parking') {
                            this.step = 'parking';
                        } else {
                            this.step = 'confirm';
                        }
                    })
                    .catch(err => {
                        Request.showErrorResponse(err, 'order_form');
                    })
                    .finally(() => {
                        utils.loading(false);
                        this.is_processing = false;
                    });
                // 非同期処理のため、ここで処理は終わる
                return;
            } else if (this.step == 'parking') {
                this.step = 'confirm';
            }
        },
        goPrevStep() {
            this.step = this.getPrevStep();
        },
        getOrderLine(type, category) {
            let result = null;
            if (this.order_lines) {
                this.order_lines.forEach(line => {
                    if (line.type == type && line.category == category) {
                        result = line;
                        return;
                    }
                });
            }
            return result;
        },
        getOrderLineFromNodeId(node_id) {
            let result = null;
            if (this.order_lines) {
                this.order_lines.forEach(line => {
                    if (line.node_id == node_id) {
                        result = line;
                        return;
                    }
                });
            }
            return result;
        },
        getOrderLineNum(line) {
            let num = 0;
            if (line) {
                const value = Number(line.num);
                if (!isNaN(value)) {
                    num = value;
                }
            }
            return num;
        },
    },
    mounted() {
        // パラメーターを取得
        const param = (<HTMLInputElement>document.getElementById('param_js')).value;
        nextTick(async () => {
            if (param) {
                // パラメーターを解析
                const p = await base64.decode(param, 'UTF-8');
                const obj = JSON.parse(p);

                // 既に注文がある場合のキー
                if (obj.encrypt_key) {
                    this.encrypt_key = obj.encrypt_key;
                }

                // 既に注文がある場合の注文情報
                if (obj.order_lines) {
                    this.order_lines = obj.order_lines;
                }

                // 申込み受付のステータス
                if (obj.accept_status) {
                    this.accept_status = obj.accept_status;
                }

                // 駐車場のみの申込みを可能にする場合は0
                if (obj.need_passport) {
                    this.need_passport = obj.need_passport;
                }

                // 参加者カテゴリの取得
                if (obj.visitor_categories) {
                    this.visitor_categories = obj.visitor_categories;

                    // 編集の場合、既存の注文からチケットの申込み数や、アトラクションの申込み数を取得
                    // ticket_num_dictはvistor_cateogryごとに、numが入っている
                    // 例{1:1,2:3}
                    // この場合は、カテゴリ1が1人、カテゴリ2が3人申込み済み
                    //
                    // child_event_num_dictはvistor_cateogryごとに、timeとnumが入っている
                    // 例{1:{time:09:00,num:1},2:{time:10:00,num:3}}
                    // この場合は、カテゴリ1が09:00に1人、カテゴリ2が10:00に3人申込み済み
                    // parking_num_dictはnode_idごとに、numが入っている
                    // 例{100:1,200:3}
                    // この場合は、node_id100が1台、node_id200が3台申込み済み
                    Object.keys(obj.visitor_categories).forEach(category => {
                        // order_linesの中で、typeが0で、categoryがkeyのもののnumを取得する
                        // 0がbase_price
                        const line = this.getOrderLine(0, category);
                        // チケットの申込み数をカテゴリごとの連想配列に格納
                        this.ticket_num_dict[category] = this.getOrderLineNum(line);
                        // ticket_num_dictの中にはカテゴリごとの以前のチケット注文数が入っている

                        // order_linesの中で、typeが1で、categoryがkeyのもののnumを取得する
                        // 1がchild_event_price(アトラクションの価格)
                        const child_line = this.getOrderLine(1, category);
                        let t = child_line ? child_line.time : null;
                        if (!t) {
                            // 時間がない場合は、最初の時間をDOMから取得
                            // inputタグでdata-categoryがcategoryかつdata-first-timeが存在するもの先頭のvalueを取得
                            const elSelect = <HTMLInputElement>(
                                document.querySelector(`input[data-category="${category}"][data-first-time]`)
                            );
                            if (elSelect) {
                                t = elSelect.value;
                            }
                        }
                        // 子イベントの申込み数を時間ごと、カテゴリごとの連想配列に格納
                        this.child_event_num_dict[category] = {
                            time: t,
                            num: this.getOrderLineNum(child_line),
                        };
                    });
                }

                // 総残数
                if (obj.total_remaining) {
                    this.total_remaining = obj.total_remaining;
                }

                // 小イベントの情報がある場合
                if (obj.child_events) {
                    this.is_child_event = obj.child_events.length > 0;
                    if (this.is_child_event) {
                        this.child_events = obj.child_events;
                    }
                }

                // 販売商品の情報がある場合
                if (obj.items) {
                    this.items = obj.items;
                }

                // 駐車場の情報がある場合
                if (obj.parking_items) {
                    this.is_parking = obj.parking_items.length > 0;
                    if (this.is_parking) {
                        this.parking_items = obj.parking_items;

                        // 駐車場の情報を駐車場ごとの連想配列に格納
                        obj.parking_items.forEach(item => {
                            const line = this.getOrderLineFromNodeId(item.node_id);
                            this.parking_num_dict[item.node_id] = this.getOrderLineNum(line);
                        });
                    }
                }

                // 駐車場の総残数
                if (obj.total_parking_remaining) {
                    this.total_parking_remaining = obj.total_parking_remaining;
                }
            }
        });
    },
});
// 申込みフォームの部分
app.mount('#app_order');
